import { AbsgArrayHelper, CacheService } from '@absg/angular-common';
import { BinProductDetail } from '@absg/api-formulary';
import { PhysicalAddress, PracticeClinicDetail, Telephone } from '@absg/api-party';
import {
  AdditionalPointInTimeData, CacheKeys, DispenseSnapshotViewModel, InventoryItem, Patient, Physician, PrintingLabel, PrintingReceipt, RequiredNote,
  SessionService,
} from '@absg/nim-client-shared';
import { Injectable } from '@angular/core';
import { StationInfoService } from 'core.services/station-info.service';
import { take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DispenseCommonOperationsService {
  private readonly printableEmptyValue = '';
  constructor(
    private stationInfoService: StationInfoService,
    private sessionService: SessionService,
    private cacheService: CacheService,
  ) { }

  getUserFirstName(additionalPointInTimeData?: AdditionalPointInTimeData): string {
    if (additionalPointInTimeData?.dispensedByFirstName) {
      return this.formatString(additionalPointInTimeData?.dispensedByFirstName);
    } else {
      let firstName = '';
      this.sessionService.getUserFirstName()
        .pipe(take(1))
        .subscribe((userFirstName: string) => {
          firstName = this.formatString(userFirstName);
        });
      return firstName;
    }
  }

  getUserLastName(additionalPointInTimeData?: AdditionalPointInTimeData): string {
    if (additionalPointInTimeData?.dispensedByLastName) {
      return this.formatString(additionalPointInTimeData?.dispensedByLastName);
    } else {
      let lastName = '';
      this.sessionService.getUserLastName()
        .pipe(take(1))
        .subscribe((userLastName: string) => {
          lastName = this.formatString(userLastName);
        });
      return lastName;
    }
  }

  getUserId(additionalPointInTimeData?: AdditionalPointInTimeData): number {
    return additionalPointInTimeData?.dispensedByUserId ?? this.sessionService.getUserId() ?? 0;
  }

  getUserName(additionalPointInTimeData?: AdditionalPointInTimeData): string {
    return additionalPointInTimeData?.dispensedByUserName ?? this.stationInfoService.getLoggedInUserName() ?? '';
  }

  getStationName(additionalPointInTimeData?: AdditionalPointInTimeData): string {
    const stationName: string = additionalPointInTimeData && additionalPointInTimeData.stationHost
      ? additionalPointInTimeData.stationHost
      : this.stationInfoService.hostName.getValue();
    return this.formatString(stationName);
  }

  formatString(value: string): string {
    return value || '';
  }

  getPracticeClinicDetail(): PracticeClinicDetail {
    const clinicDetail = this.stationInfoService.practiceClinicDetail.getValue();
    return clinicDetail;
  }

  mapAttendingPhysician(sourceAttendingPhysician: Physician,
    destinationLabel: PrintingLabel | DispenseSnapshotViewModel | PrintingReceipt): void {
    if (sourceAttendingPhysician) {
      destinationLabel.attendingPhysicianFirstName = this.formatString(sourceAttendingPhysician.firstName);
      destinationLabel.attendingPhysicianLastName = this.formatString(sourceAttendingPhysician.lastName);
    } else {
      destinationLabel.attendingPhysicianFirstName = this.printableEmptyValue;
      destinationLabel.attendingPhysicianLastName = this.printableEmptyValue;
    }
  }

  mapPatient(sourcePatient: Patient, printingLabel: PrintingLabel | DispenseSnapshotViewModel | PrintingReceipt): void {
    if (sourcePatient) {
      printingLabel.patientFirstName = this.formatString(sourcePatient.firstName);
      printingLabel.patientLastName = this.formatString(sourcePatient.lastName);
      printingLabel.patientId = this.formatString(sourcePatient.patientId);
      printingLabel.dateOfBirth = sourcePatient.dateOfBirth;
      printingLabel.allergies = sourcePatient.allergies;
    } else {
      printingLabel.patientFirstName = this.printableEmptyValue;
      printingLabel.patientLastName = this.printableEmptyValue;
      printingLabel.patientId = this.printableEmptyValue;
      printingLabel.dateOfBirth = null;
      printingLabel.allergies = [];
    }
  }

  mapClinic(sourceClinic: PracticeClinicDetail, printingLabel: PrintingLabel | DispenseSnapshotViewModel): void {
    if (sourceClinic) {
      printingLabel.clinicName = this.formatString(sourceClinic.clinicName);
      // TODO: Strongly type the Clinic references as soon as we resolve the data transfer issues
      const clinicAddress = this.getPrintableClinicAddress(sourceClinic);
      if (clinicAddress) {
        // NOTE: Only line 1 of the address is displayed in the label
        printingLabel.clinicAddressLine1 = this.formatString(clinicAddress.address1);
        printingLabel.clinicCity = this.formatString(clinicAddress.city);
        printingLabel.clinicState = this.formatString(clinicAddress.state);
        printingLabel.clinicZipCode = this.formatString(clinicAddress.zip);
      } else {
        printingLabel.clinicAddressLine1 = this.printableEmptyValue;
        printingLabel.clinicCity = this.printableEmptyValue;
        printingLabel.clinicState = this.printableEmptyValue;
        printingLabel.clinicZipCode = this.printableEmptyValue;
      }
      const clinicPhoneNumber = this.getPrintableClinicPhoneNumber(sourceClinic);
      printingLabel.clinicPhoneNumber = clinicPhoneNumber
        ? this.formatString(clinicPhoneNumber.contactNumber)
        : this.printableEmptyValue;
      printingLabel.practiceName = this.formatString(sourceClinic.practiceName);
    } else {
      printingLabel.clinicName = this.printableEmptyValue;
      printingLabel.clinicAddressLine1 = this.printableEmptyValue;
      printingLabel.clinicCity = this.printableEmptyValue;
      printingLabel.clinicState = this.printableEmptyValue;
      printingLabel.clinicZipCode = this.printableEmptyValue;
      printingLabel.clinicPhoneNumber = this.printableEmptyValue;
      printingLabel.practiceName = this.printableEmptyValue;
    }
  }

  mapClinicForReceipt(sourceClinic: PracticeClinicDetail, destinationReceipt: PrintingReceipt): void {
    if (sourceClinic) {
      destinationReceipt.practiceName = this.formatString(sourceClinic.practiceName);
      destinationReceipt.clinicName = this.formatString(sourceClinic.clinicName);
    } else {
      destinationReceipt.practiceName = this.printableEmptyValue;
      destinationReceipt.clinicName = this.printableEmptyValue;
    }
  }

  getPrintableClinicAddress(clinic: PracticeClinicDetail): PhysicalAddress {
    const clinicAddresses: PhysicalAddress[] = clinic.addresses;
    let clinicAddress: PhysicalAddress = null;
    if (Array.isArray(clinicAddresses) && clinicAddresses.length > 0) {
      clinicAddress = clinicAddresses[0];
    }
    return clinicAddress;
  }

  getPrintableClinicPhoneNumber(clinic: PracticeClinicDetail): Telephone {
    const clinicPhoneNumbers: Telephone[] = clinic.phoneNumbers;
    let clinicPhoneNumber: Telephone = null;
    if (Array.isArray(clinicPhoneNumbers) && clinicPhoneNumbers.length > 0) {
      clinicPhoneNumber = clinicPhoneNumbers[0];
    }
    return clinicPhoneNumber;
  }

  mapMEGGroupReceiptAndLabel(inventoryItem: InventoryItem): InventoryItem {
    const data = this.cacheService.get(CacheKeys.FormularyBinGroupList.toString());
    if (!AbsgArrayHelper.isNil(data) && !AbsgArrayHelper.isNil(inventoryItem)) {
      const binProductDetails: BinProductDetail[] = data.getValue();
      const binProduct = AbsgArrayHelper.find(binProductDetails, y => y.supplierProductInclusionId === inventoryItem.meiParentId);
      if (!AbsgArrayHelper.isNil(binProduct) && binProduct.useMegNotes) {
        inventoryItem.infuseNotes1 = binProduct.infuseNotes1;
        inventoryItem.infuseNotes2 = binProduct.infuseNotes2;
        inventoryItem.infuseNotes3 = binProduct.infuseNotes3;
        inventoryItem.infusionNoteOnLabel = binProduct.infusionNoteOnLabel;
        inventoryItem.infusionNoteOnReceipt = binProduct.infusionNoteOnReceipt;
        if (binProduct.enableRequireNotes) {
          inventoryItem.requiredNote = new RequiredNote(binProduct.requireNotes,
            inventoryItem.requiredNote.note, binProduct.enableRequireNotes, binProduct.requireNotesOnLabel, binProduct.requireNotesOnReceipt);
          inventoryItem.requiredNoteOnLabel = binProduct.requireNotesOnLabel;
          inventoryItem.requiredNoteOnReceipt = binProduct.requireNotesOnReceipt;
        } else {
          inventoryItem.requiredNote = new RequiredNote('', null, null, false, false);
          inventoryItem.requiredNoteOnLabel = false;
          inventoryItem.requiredNoteOnReceipt = false;
        }
      }
    }
    return inventoryItem;
  }
}
